import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setFilter, setAllFilters, clearFilters, setView, toggleFilterPanel,
  selectFilterOptions,
} from '../../redux/slices/selectionSlice';
import { Autocomplete, TextField, Checkbox, Chip } from '@mui/material';
import { GridView, TableRows, TuneRounded, Close } from '@mui/icons-material';

// ── Applied filter chip ───────────────────────────────────────
const AppliedChip = ({ label, value }) => (
  <span className="sel-applied-chip">
    <span className="sel-applied-chip-label">{label}:</span>
    <span className="sel-applied-chip-value">{value}</span>
  </span>
);

// ── Controlled Multi-select ───────────────────────────────────
const MultiSelect = ({ label, value, onChange, options }) => (
  <div className="sel-drawer-filter-item">
    <Autocomplete
      multiple size="small" options={options} value={value}
      onChange={(_, val) => onChange(val)}
      disableCloseOnSelect
      renderOption={(props, option, { selected }) => (
        <li {...props} style={{ fontSize: 12, padding: '2px 8px' }}>
          <Checkbox size="small" checked={selected} style={{ padding: 2, marginRight: 4 }} />
          {option}
        </li>
      )}
      renderInput={params => (
        <TextField {...params} label={label}
          InputLabelProps={{ style: { fontSize: 12 } }}
          inputProps={{ ...params.inputProps, style: { fontSize: 12 } }}
        />
      )}
      renderTags={(vals, getTagProps) =>
        vals.map((v, i) => (
          <Chip key={v} label={v} size="small" {...getTagProps({ index: i })}
            style={{ height: 18, fontSize: 11 }} />
        ))
      }
      sx={{ '& .MuiOutlinedInput-root': { fontSize: 12, minHeight: 36 } }}
    />
  </div>
);

// ── Controlled Range filter ───────────────────────────────────
const RangeFilter = ({ label, minVal, maxVal, onMinChange, onMaxChange }) => (
  <div className="sel-drawer-filter-item">
    <div className="sel-range-group">
      <span className="sel-range-label">{label}</span>
      <div className="sel-range-inputs">
        <input type="number" className="sel-range-input" placeholder="Min"
          value={minVal} onChange={e => onMinChange(e.target.value)} />
        <span className="sel-range-sep">–</span>
        <input type="number" className="sel-range-input" placeholder="Max"
          value={maxVal} onChange={e => onMaxChange(e.target.value)} />
      </div>
    </div>
  </div>
);

const EMPTY_DRAFT = {
  shapes: [], colors: [], clarities: [], suppliers: [],
  caratRanges: [], demands: [], locations: [], buckets: [],
  needsMin: '', needsMax: '', grpNeedsMin: '', grpNeedsMax: '',
  invCountMin: '', invCountMax: '', salesCountMin: '', salesCountMax: '',
    grpSalesCountMin: '', grpSalesCountMax: '',
  grpInvCountMax: '', grpInvCountMin: '',
  ytdSalesCountMin: '', ytdSalesCountMax: '',
  grpYtdSalesCountMin: '', grpYtdSalesCountMax: '',
  d90SalesCountMin: '', d90SalesCountMax: '',
  grp90DSalesCountMin: '', grp90DSalesCountMax: '', users: []
};

// ── Main FilterPanel ──────────────────────────────────────────
const FilterPanel = () => {
  const dispatch      = useDispatch();
  const { filterPanelOpen, view, filters } = useSelector(s => s.selection);
  const filterOptions = useSelector(selectFilterOptions);
  const activeCount   = countActiveFilters(filters);

  const [draft, setDraft] = useState(filters);

  // Re-sync draft whenever drawer opens
  useEffect(() => {
    if (filterPanelOpen) setDraft(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPanelOpen]);

  const set = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  const handleApply = () => {
    dispatch(setAllFilters(draft));
    if (filterPanelOpen) dispatch(toggleFilterPanel());
  };

  const handleClear = () => {
    setDraft(EMPTY_DRAFT);
    dispatch(clearFilters());
    if (filterPanelOpen) dispatch(toggleFilterPanel());
  };

  const handleSearch = useCallback(e => {
    dispatch(setFilter({ key: 'search', value: e.target.value }));
  }, [dispatch]);

  return (
    <>
      {/* ── White container (always visible) ── */}
      <div className="sel-filter-container">

        {/* Row 1: Applied filter chips (read-only display) */}
        {activeCount > 0 && (
          <div className="sel-applied-chips">
            {filters.shapes.length      > 0 && <AppliedChip label="Shape"    value={filters.shapes.join(', ')} />}
            {filters.colors.length      > 0 && <AppliedChip label="Color"    value={filters.colors.join(', ')} />}
            {filters.clarities.length   > 0 && <AppliedChip label="Clarity"  value={filters.clarities.join(', ')} />}
            {filters.suppliers.length   > 0 && <AppliedChip label="Supplier" value={filters.suppliers.join(', ')} />}
            {filters.caratRanges.length > 0 && <AppliedChip label="Size"     value={filters.caratRanges.join(', ')} />}
            {filters.demands.length     > 0 && <AppliedChip label="Demand"   value={filters.demands.join(', ')} />}
            {filters.locations.length   > 0 && <AppliedChip label="Location" value={filters.locations.join(', ')} />}
            {filters.buckets.length     > 0 && <AppliedChip label="Bucket"   value={filters.buckets.join(', ')} />}
            {filters.users.length       > 0 && <AppliedChip label="User" value={filters.users.join(', ')} />}
            {(filters.needsMin      || filters.needsMax)      && <AppliedChip label="Need(2X)"  value={`${filters.needsMin || '0'}–${filters.needsMax || '∞'}`} />}
            {(filters.grpNeedsMin   || filters.grpNeedsMax)   && <AppliedChip label="Grp Need"  value={`${filters.grpNeedsMin || '0'}–${filters.grpNeedsMax || '∞'}`} />}
            {(filters.invCountMin   || filters.invCountMax)   && <AppliedChip label="Inventory" value={`${filters.invCountMin || '0'}–${filters.invCountMax || '∞'}`} />}
            {(filters.grpInvCountMin   || filters.grpInvCountMax)   && <AppliedChip label="Grp Inventory" value={`${filters.grpInvCountMin || '0'}–${filters.grpInvCountMax || '∞'}`} />}
            {(filters.salesCountMin || filters.salesCountMax) && <AppliedChip label="1Y Sales"  value={`${filters.salesCountMin || '0'}–${filters.salesCountMax || '∞'}`} />}
            {(filters.grpSalesCountMin || filters.grpSalesCountMax) && <AppliedChip label="Grp 1Y Sales"  value={`${filters.grpSalesCountMin || '0'}–${filters.grpSalesCountMax || '∞'}`} />}
            {(filters.ytdSalesCountMin || filters.ytdSalesCountMax) && <AppliedChip label="YTD Sales"  value={`${filters.ytdSalesCountMin || '0'}–${filters.ytdSalesCountMax || '∞'}`} />}
            {(filters.grpYtdSalesCountMin || filters.grpYtdSalesCountMax) && <AppliedChip label="Grp YTD Sales"  value={`${filters.grpYtdSalesCountMin || '0'}–${filters.grpYtdSalesCountMax || '∞'}`} />}
            {(filters.d90SalesCountMin || filters.d90SalesCountMax) && <AppliedChip label="90D Sales"  value={`${filters.d90SalesCountMin || '0'}–${filters.d90SalesCountMax || '∞'}`} />}
            {(filters.grp90DSalesCountMin || filters.grp90DSalesCountMax) && <AppliedChip label="Grp 90D Sales"  value={`${filters.grp90DSalesCountMin || '0'}–${filters.grp90DSalesCountMax || '∞'}`} />}
          </div>
        )}

        {/* Row 2: Search + view toggle + Filters button */}
        <div className="sel-topbar">
          <div className="sel-search-input">
            <TextField
              size="small" fullWidth
              placeholder="Search by ID, Stock No, Cert No, shape, color, supplier…"
              value={filters.search}
              onChange={handleSearch}
              inputProps={{ style: { fontSize: 13 } }}
            />
          </div>
          <div className="sel-topbar-right">
            <div className="sel-view-toggle">
              <GridView
                className={'sel-view-icon' + (view === 'card'  ? ' active' : '')}
                onClick={() => dispatch(setView('card'))}
                titleAccess="Grid View"
              />
              <TableRows
                className={'sel-view-icon' + (view === 'table' ? ' active' : '')}
                onClick={() => dispatch(setView('table'))}
                titleAccess="Table View"
              />
            </div>
            <button
              className={'sel-filter-trigger-btn' + (activeCount > 0 ? ' has-active' : '')}
              onClick={() => dispatch(toggleFilterPanel())}
            >
              <TuneRounded style={{ fontSize: 16 }} />
              Filters
              {activeCount > 0 && <span className="sel-filter-count-badge">{activeCount}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* ── Right drawer ── */}
      {filterPanelOpen && (
        <>
          <div className="sel-drawer-backdrop" onClick={() => dispatch(toggleFilterPanel())} />
          <div className="sel-filter-drawer">
            <div className="sel-drawer-header">
              <span className="sel-drawer-title">Filters</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="sel-drawer-clear-btn" onClick={handleClear}>Clear all</button>
                <button className="sel-drawer-close-btn" onClick={() => dispatch(toggleFilterPanel())}>
                  <Close fontSize="small" />
                </button>
              </div>
            </div>

            <div className="sel-drawer-body">
              <MultiSelect label="Shape"    value={draft.shapes}      onChange={v => set('shapes', v)}      options={filterOptions.shapes} />
              <MultiSelect label="Color"    value={draft.colors}      onChange={v => set('colors', v)}      options={filterOptions.colors} />
              <MultiSelect label="Clarity"  value={draft.clarities}   onChange={v => set('clarities', v)}   options={filterOptions.clarities} />
              <MultiSelect label="Supplier" value={draft.suppliers}   onChange={v => set('suppliers', v)}   options={filterOptions.suppliers} />
              <MultiSelect label="Size"     value={draft.caratRanges} onChange={v => set('caratRanges', v)} options={filterOptions.caratRanges} />
              <MultiSelect label="Demand"   value={draft.demands}     onChange={v => set('demands', v)}     options={filterOptions.demands} />
              <MultiSelect label="Location" value={draft.locations}   onChange={v => set('locations', v)}   options={filterOptions.locations} />
              <MultiSelect label="Bucket"   value={draft.buckets}     onChange={v => set('buckets', v)}     options={filterOptions.buckets} />
              <MultiSelect label="User" value={draft.users} onChange={v => set('users', v)} options={filterOptions.users} />

              <div className="sel-drawer-divider">Range Filters</div>
              <RangeFilter label="Needs(2X)"
                minVal={draft.needsMin}      maxVal={draft.needsMax}
                onMinChange={v => set('needsMin', v)}      onMaxChange={v => set('needsMax', v)} />
              <RangeFilter label="Group Needs(2X)"
                minVal={draft.grpNeedsMin}   maxVal={draft.grpNeedsMax}
                onMinChange={v => set('grpNeedsMin', v)}   onMaxChange={v => set('grpNeedsMax', v)} />
              <RangeFilter label="Inventory"
                minVal={draft.invCountMin}   maxVal={draft.invCountMax}
                onMinChange={v => set('invCountMin', v)}   onMaxChange={v => set('invCountMax', v)} />
              <RangeFilter label="Group Inventory"
                minVal={draft.grpInvCountMin}   maxVal={draft.grpInvCountMax}
                onMinChange={v => set('grpInvCountMin', v)}   onMaxChange={v => set('grpInvCountMax', v)} />  
              <RangeFilter label="Sales"
                minVal={draft.salesCountMin} maxVal={draft.salesCountMax}
                onMinChange={v => set('salesCountMin', v)} onMaxChange={v => set('salesCountMax', v)} />
              <RangeFilter label="Grouped Sales"
                minVal={draft.grpSalesCountMin} maxVal={draft.grpSalesCountMax}
                onMinChange={v => set('grpSalesCountMin', v)} onMaxChange={v => set('grpSalesCountMax', v)} />  
              <RangeFilter label="YTD Sales"
                minVal={draft.ytdSalesCountMin} maxVal={draft.ytdSalesCountMax}
                onMinChange={v => set('ytdSalesCountMin', v)} onMaxChange={v => set('ytdSalesCountMax', v)} />
              <RangeFilter label="YTD Sales (Grouped)"
                minVal={draft.grpYtdSalesCountMin} maxVal={draft.grpYtdSalesCountMax}
                onMinChange={v => set('grpYtdSalesCountMin', v)} onMaxChange={v => set('grpYtdSalesCountMax', v)} /> 
              <RangeFilter label="Sales 90D"
                minVal={draft.d90SalesCountMin} maxVal={draft.d90SalesCountMax}
                onMinChange={v => set('d90SalesCountMin', v)} onMaxChange={v => set('d90SalesCountMax', v)} /> 
              <RangeFilter label="Sales 90D (Grouped)"
                minVal={draft.grp90DSalesCountMin} maxVal={draft.grp90DSalesCountMax}
                onMinChange={v => set('grp90DSalesCountMin', v)} onMaxChange={v => set('grp90DSalesCountMax', v)} />   



            </div>

            <div className="sel-drawer-footer">
              <button className="sel-drawer-apply-btn" onClick={handleApply}>
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

function countActiveFilters(filters) {
  let n = 0;
  if (filters.shapes.length)       n++;
  if (filters.colors.length)       n++;
  if (filters.clarities.length)    n++;
  if (filters.suppliers.length)    n++;
  if (filters.users.length)        n++;
  if (filters.caratRanges.length)  n++;
  if (filters.demands.length)      n++;
  if (filters.locations.length)    n++;
  if (filters.buckets.length)      n++;
  if (filters.needsMin      || filters.needsMax)      n++;
  if (filters.grpNeedsMin   || filters.grpNeedsMax)   n++;
  if (filters.invCountMin   || filters.invCountMax)   n++;
  if (filters.grpInvCountMin   || filters.grpInvCountMax)   n++;
  if (filters.salesCountMin || filters.salesCountMax) n++;
  if (filters.grpSalesCountMin || filters.grpSalesCountMax) n++;
  if (filters.ytdSalesCountMin || filters.ytdSalesCountMax) n++;
  if (filters.grpYtdSalesCountMin || filters.grpYtdSalesCountMax) n++;
  if (filters.d90SalesCountMin || filters.d90SalesCountMax) n++;
  if (filters.grp90DSalesCountMin || filters.grp90DSalesCountMax) n++;
  return n;
}

export default FilterPanel;
